/**
 * POSTEINGANG_MOCK - Eingänge over all four channels, shaped exactly like the wire.
 *
 * Field-for-field a `PosteingangItem`, including the example row documented at the top
 * of `packages/shared/src/posteingang.ts`. The api routes are B1 step 3 and not deployed,
 * so this is what the Eingangsliste renders until the flip; keeping it wire-shaped means
 * the screen needs no change when the real rows arrive.
 *
 * The upload and scanning_center rows are here so the Kanal filter and the row chrome are
 * exercised for all four channels. Live, both legitimately return an EMPTY page today -
 * nothing writes communication rows on those channels yet (Jevin, B1 handover) - so an
 * empty result there is the correct answer, not a broken filter.
 *
 * The `status` values here obey POSTEINGANG_STATUS_SQL rather than being picked freely:
 * zero tasks reads as `offen`, `erledigt` only when every task is done or cancelled, and
 * `freigegeben` never appears - it has no producer until the staged Freigabe (epic E1).
 * `eingang_status` starts where the 0141 backfill would; mock changes live in memory.
 *
 * [Source: packages/shared/src/posteingang.ts] (the contract + its example row)
 */
import type {
  AttachmentView,
  AuditEntry,
  PosteingangActivityEntry,
  PosteingangItem,
  PosteingangPolicenkontrolleEintrag,
  PosteingangSentMail,
  PosteingangState,
} from '@brokly/shared';
import { MOCK_USER_A, MOCK_USER_B } from '../administration/mock';
import { inDays } from '../aufgaben/mock';
import type { TaskRow } from '../aufgaben/types';
import type { AccessRequestItem } from '../../lib/api-client';

export const POSTEINGANG_MOCK: PosteingangItem[] = [
  {
    id: 'IN-1',
    kanal: 'email',
    status: 'freigabe_ausstehend',
    eingang_status: 'offen',
    tasks: { total: 2, offen: 2, freigabe_ausstehend: 2, erledigt: 0, automatisch: 0 },
    attachment_count: 2,
    attachment_mime_types: ['application/pdf'],
    confidence: '0.920',
    received_at: '2026-09-07T06:14:00.000Z',
    subject: 'Schaden Fahrzeug + Werkstattrechnung',
    from: 'fam.bauer@bluewin.ch',
    to: 'schaden@broker.ch',
    cc: null,
    sync_level_captured: 1,
    task_status: 'awaiting_approval',
    task_routing: 'freigabe',
    subject_type: 'firma',
    subject_id: 'firma-bauer',
    kunde_name: 'Bauer GmbH',
    your_effective_tier: 'full',
    is_private: false,
    can_mark_private: true,
    restricted_to_team: null,
    unconfirmed: null,
    // The api always sends `unread`; the fixture carries it so mock mode shows the state.
    unread: true,
  },
  {
    id: 'IN-2',
    kanal: 'scanning_center',
    status: 'offen',
    eingang_status: 'offen',
    tasks: { total: 1, offen: 1, freigabe_ausstehend: 0, erledigt: 0, automatisch: 0 },
    attachment_count: 4,
    attachment_mime_types: ['application/pdf'],
    confidence: '0.640',
    received_at: '2026-09-07T05:02:00.000Z',
    subject: 'Postmappe 07.09. - Policenkopien Helvetia',
    from: 'scan-center@broker.ch',
    to: 'post@broker.ch',
    cc: null,
    sync_level_captured: null,
    task_status: 'open',
    task_routing: 'manual',
    subject_type: null,
    subject_id: null,
    kunde_name: null,
    your_effective_tier: 'full',
    is_private: false,
    can_mark_private: false,
    restricted_to_team: null,
    unconfirmed: null,
  },
  {
    id: 'IN-3',
    kanal: 'portal',
    status: 'in_bearbeitung',
    eingang_status: 'offen',
    tasks: { total: 1, offen: 1, freigabe_ausstehend: 0, erledigt: 0, automatisch: 0 },
    attachment_count: 0,
    attachment_mime_types: [],
    confidence: '0.880',
    received_at: '2026-09-06T16:41:00.000Z',
    subject: 'Adressänderung Familie Keller',
    from: 'm.keller@gmx.ch',
    to: 'portal@broker.ch',
    cc: null,
    sync_level_captured: null,
    task_status: 'in_progress',
    task_routing: 'freigabe',
    subject_type: 'person',
    subject_id: 'person-keller',
    kunde_name: 'Markus Keller',
    your_effective_tier: 'full',
    is_private: false,
    can_mark_private: true,
    restricted_to_team: null,
    // An unconfirmed record IS a customer record, so the wire carries
    // subject_type/subject_id/kunde_name for it exactly as for a known client;
    // `unconfirmed` is the only thing that separates the two, and the api sets it ONLY
    // together with a non-empty kunde_name.
    unconfirmed: { name: 'Markus Keller' },
  },
  {
    id: 'IN-4',
    kanal: 'upload',
    status: 'offen',
    eingang_status: 'offen',
    tasks: { total: 0, offen: 0, freigabe_ausstehend: 0, erledigt: 0, automatisch: 0 },
    attachment_count: 1,
    attachment_mime_types: ['application/pdf'],
    confidence: null,
    received_at: '2026-09-06T11:20:00.000Z',
    subject: 'Manueller Upload: Unterschriebenes Mandat',
    from: 'v.tanner@broker.ch',
    to: null,
    cc: null,
    sync_level_captured: null,
    task_status: null,
    task_routing: null,
    subject_type: 'firma',
    subject_id: 'firma-tanner',
    kunde_name: 'Tanner Bau AG',
    your_effective_tier: 'full',
    is_private: false,
    can_mark_private: false,
    restricted_to_team: null,
    unconfirmed: null,
  },
  {
    id: 'IN-5',
    kanal: 'email',
    status: 'erledigt',
    eingang_status: 'erledigt',
    tasks: { total: 1, offen: 0, freigabe_ausstehend: 0, erledigt: 1, automatisch: 1 },
    attachment_count: 1,
    attachment_mime_types: ['application/pdf'],
    confidence: '0.960',
    received_at: '2026-09-05T09:05:00.000Z',
    subject: 'Prämienrechnung 2026 - automatisch verarbeitet',
    from: 'billing@helvetia.ch',
    to: 'rechnungen@broker.ch',
    cc: null,
    sync_level_captured: 1,
    task_status: 'done',
    task_routing: 'automated',
    subject_type: 'firma',
    subject_id: 'firma-bauer',
    kunde_name: 'Bauer GmbH',
    your_effective_tier: 'full',
    is_private: false,
    can_mark_private: true,
    restricted_to_team: null,
    unconfirmed: null,
  },
  {
    id: 'IN-6',
    kanal: 'email',
    status: 'erledigt',
    eingang_status: 'erledigt',
    tasks: { total: 2, offen: 0, freigabe_ausstehend: 0, erledigt: 2, automatisch: 0 },
    attachment_count: 0,
    attachment_mime_types: [],
    confidence: '0.710',
    received_at: '2026-09-04T14:32:00.000Z',
    subject: 'Kündigung Hausratversicherung - erledigt',
    from: 's.brunner@bluewin.ch',
    to: 'info@broker.ch',
    cc: null,
    sync_level_captured: 1,
    task_status: 'done',
    task_routing: 'freigabe',
    subject_type: 'person',
    subject_id: 'person-brunner',
    kunde_name: 'Sandra Brunner',
    your_effective_tier: 'full',
    is_private: false,
    can_mark_private: true,
    restricted_to_team: 'Vertraulich',
    unconfirmed: null,
  },
];

// ── Die Aufgaben je Eingang ───────────────────────────────────────────────────

/**
 * The tasks each Eingang produced, comm_id-linked to the rows above. One fixture with
 * them: every `tasks` aggregate on a row IS the count of the rows here carrying that
 * comm_id, derived as POSTEINGANG_STATUS_SQL does it (offen = not done/cancelled,
 * freigabe_ausstehend = awaiting_approval, automatisch = routing 'automated').
 * subject/from/received_at echo the Eingang, because taskSplitViewMock builds Teil 1's
 * mail from the selected TASK. Separate from AUFGABEN_MOCK, which is the /aufgaben queue.
 */
const EINGANG_TASKS_MOCK: TaskRow[] = [
  {
    id: 'task-in1-schaden',
    process: 'email_intake',
    status: 'awaiting_approval',
    routing: 'freigabe',
    title: 'Schadenmeldung erfassen (Fahrzeug)',
    comm_id: 'IN-1',
    assigned_to: MOCK_USER_A,
    assigned_group_id: null,
    assignee_name: 'Lara Meier',
    assigned_group_name: null,
    due_at: inDays(1),
    completed_at: null,
    needs_review: true,
    subject_type: 'firma',
    subject_id: 'firma-bauer',
    confidence: '0.920',
    kunde_name: 'Bauer GmbH',
    subject: 'Schaden Fahrzeug + Werkstattrechnung',
    from: 'fam.bauer@bluewin.ch',
    received_at: '2026-09-07T06:14:00.000Z',
    created_at: '2026-09-07T06:15:00.000Z',
    updated_at: '2026-09-07T08:30:00.000Z',
  },
  {
    id: 'task-in1-rechnung',
    process: 'invoice_check',
    status: 'awaiting_approval',
    routing: 'freigabe',
    title: 'Werkstattrechnung prüfen',
    comm_id: 'IN-1',
    assigned_to: MOCK_USER_A,
    assigned_group_id: null,
    assignee_name: 'Lara Meier',
    assigned_group_name: null,
    due_at: inDays(2),
    completed_at: null,
    needs_review: true,
    subject_type: 'firma',
    subject_id: 'firma-bauer',
    confidence: '0.780',
    kunde_name: 'Bauer GmbH',
    subject: 'Schaden Fahrzeug + Werkstattrechnung',
    from: 'fam.bauer@bluewin.ch',
    received_at: '2026-09-07T06:14:00.000Z',
    created_at: '2026-09-07T06:15:00.000Z',
    updated_at: '2026-09-07T08:30:00.000Z',
  },
  {
    id: 'task-in2-policenkopien',
    process: 'doc_classification',
    status: 'open',
    routing: 'manual',
    title: 'Policenkopien Helvetia zuordnen',
    comm_id: 'IN-2',
    assigned_to: null,
    assigned_group_id: 'grp-backoffice',
    assignee_name: null,
    assigned_group_name: 'Backoffice',
    due_at: inDays(3),
    completed_at: null,
    needs_review: false,
    subject_type: null,
    subject_id: null,
    confidence: '0.640',
    kunde_name: null,
    subject: 'Postmappe 07.09. - Policenkopien Helvetia',
    from: 'scan-center@broker.ch',
    received_at: '2026-09-07T05:02:00.000Z',
    created_at: '2026-09-07T05:10:00.000Z',
    updated_at: '2026-09-07T05:10:00.000Z',
  },
  {
    id: 'task-in3-adresse',
    process: 'portal_mutation',
    status: 'in_progress',
    routing: 'manual',
    title: 'Adressänderung erfassen',
    comm_id: 'IN-3',
    assigned_to: MOCK_USER_B,
    assigned_group_id: null,
    assignee_name: 'Tim Roth',
    assigned_group_name: null,
    due_at: inDays(1),
    completed_at: null,
    needs_review: false,
    subject_type: 'person',
    subject_id: 'person-keller',
    confidence: '0.880',
    kunde_name: 'Markus Keller',
    subject: 'Adressänderung Familie Keller',
    from: 'm.keller@gmx.ch',
    received_at: '2026-09-06T16:41:00.000Z',
    created_at: '2026-09-06T16:45:00.000Z',
    updated_at: '2026-09-07T09:12:00.000Z',
  },
  {
    id: 'task-in5-praemie',
    process: 'invoice_check',
    status: 'done',
    routing: 'automated',
    title: 'Prämienrechnung 2026 verbucht',
    comm_id: 'IN-5',
    assigned_to: null,
    assigned_group_id: null,
    assignee_name: null,
    assigned_group_name: null,
    due_at: null,
    completed_at: '2026-09-05T09:06:00.000Z',
    needs_review: false,
    subject_type: 'firma',
    subject_id: 'firma-bauer',
    confidence: '0.960',
    kunde_name: 'Bauer GmbH',
    subject: 'Prämienrechnung 2026 - automatisch verarbeitet',
    from: 'billing@helvetia.ch',
    received_at: '2026-09-05T09:05:00.000Z',
    created_at: '2026-09-05T09:05:00.000Z',
    updated_at: '2026-09-05T09:06:00.000Z',
  },
  {
    id: 'task-in6-kuendigung',
    process: 'status_change',
    status: 'done',
    routing: 'manual',
    title: 'Kündigung bestätigen',
    comm_id: 'IN-6',
    assigned_to: MOCK_USER_A,
    assigned_group_id: null,
    assignee_name: 'Lara Meier',
    assigned_group_name: null,
    due_at: null,
    completed_at: '2026-09-04T16:02:00.000Z',
    needs_review: false,
    subject_type: 'person',
    subject_id: 'person-brunner',
    confidence: '0.710',
    kunde_name: 'Sandra Brunner',
    subject: 'Kündigung Hausratversicherung - erledigt',
    from: 's.brunner@bluewin.ch',
    received_at: '2026-09-04T14:32:00.000Z',
    created_at: '2026-09-04T14:35:00.000Z',
    updated_at: '2026-09-04T16:02:00.000Z',
  },
  {
    id: 'task-in6-police',
    process: 'policy_endorsement_review',
    status: 'done',
    routing: 'manual',
    title: 'Police auf inaktiv setzen',
    comm_id: 'IN-6',
    assigned_to: MOCK_USER_A,
    assigned_group_id: null,
    assignee_name: 'Lara Meier',
    assigned_group_name: null,
    due_at: null,
    completed_at: '2026-09-04T16:20:00.000Z',
    needs_review: false,
    subject_type: 'person',
    subject_id: 'person-brunner',
    confidence: '0.690',
    kunde_name: 'Sandra Brunner',
    subject: 'Kündigung Hausratversicherung - erledigt',
    from: 's.brunner@bluewin.ch',
    received_at: '2026-09-04T14:32:00.000Z',
    created_at: '2026-09-04T14:35:00.000Z',
    updated_at: '2026-09-04T16:20:00.000Z',
  },
];

/** The tasks of ONE Eingang - the mock leg of `useEingangTasks`. */
export function eingangTasksMock(commId: string): TaskRow[] {
  return EINGANG_TASKS_MOCK.filter((t) => t.comm_id === commId);
}

// ── Die Dateien je Eingang ────────────────────────────────────────────────────

/**
 * The files each Eingang carries. Each row's attachment_count and mime types are the
 * count and types of the entries here; taskSplitViewMock used to hand the same three
 * files to every task, which contradicted every count in POSTEINGANG_MOCK.
 * Ids end in `-pdf` - that is what mockDocumentUrl resolves to the sample PDF.
 */
const EINGANG_ATTACHMENTS_MOCK: Record<string, AttachmentView[]> = {
  'IN-1': [
    {
      id: 'doc-in1-schadenmeldung-pdf',
      filename: 'Schadenmeldung_Fahrzeug.pdf',
      mime_type: 'application/pdf',
      size_bytes: 412_000,
    },
    {
      id: 'doc-in1-werkstattrechnung-pdf',
      filename: 'Werkstattrechnung_4471.pdf',
      mime_type: 'application/pdf',
      size_bytes: 184_000,
    },
  ],
  'IN-2': [
    {
      id: 'doc-in2-police-a-pdf',
      filename: 'Police_Helvetia_884-1201.pdf',
      mime_type: 'application/pdf',
      size_bytes: 2_412_000,
    },
    {
      id: 'doc-in2-police-b-pdf',
      filename: 'Police_Helvetia_884-1202.pdf',
      mime_type: 'application/pdf',
      size_bytes: 1_980_000,
    },
    {
      id: 'doc-in2-police-c-pdf',
      filename: 'Police_Helvetia_884-1203.pdf',
      mime_type: 'application/pdf',
      size_bytes: 2_104_000,
    },
    {
      id: 'doc-in2-begleitblatt-pdf',
      filename: 'Begleitblatt_Postmappe.pdf',
      mime_type: 'application/pdf',
      size_bytes: 96_000,
    },
  ],
  // IN-3 (Adressänderung über das Portal) and IN-6 (Kündigung per Mail) carry no files -
  // and the Maske must show that, not three borrowed ones.
  'IN-3': [],
  'IN-4': [
    {
      id: 'doc-in4-mandat-pdf',
      filename: 'Maklermandat_unterschrieben.pdf',
      mime_type: 'application/pdf',
      size_bytes: 748_000,
    },
  ],
  'IN-5': [
    {
      id: 'doc-in5-praemienrechnung-pdf',
      filename: 'Praemienrechnung_2026.pdf',
      mime_type: 'application/pdf',
      size_bytes: 168_000,
    },
  ],
  'IN-6': [],
};

/** The files of ONE Eingang; empty for an id the fixture does not know. */
export function eingangAttachmentsMock(commId: string): AttachmentView[] {
  return EINGANG_ATTACHMENTS_MOCK[commId] ?? [];
}

// ── Audit-Trail je Eingang ────────────────────────────────────────────────────

/**
 * Audit rows per entity (communication or task). Only for things that actually happened
 * in the rows above: IN-1's tasks are awaiting_approval, so their trail stops at routing.
 * severity 'high' follows J1 (release, attachment deactivate).
 */
const EINGANG_AUDIT_MOCK: Record<string, AuditEntry[]> = {
  'IN-1': [
    {
      id: '9001',
      actor_kind: 'system',
      user_id: null,
      action: 'create',
      entity_type: 'communication',
      entity_id: 'IN-1',
      before: null,
      after: { channel: 'email', from: 'fam.bauer@bluewin.ch' },
      context: { source: 'intake.worker' },
      severity: 'normal',
      created_at: '2026-09-07T06:14:12.000Z',
    },
    {
      id: '9002',
      actor_kind: 'ai',
      user_id: null,
      action: 'enrichment',
      entity_type: 'communication',
      entity_id: 'IN-1',
      before: { kunde_name: null },
      after: { kunde_name: 'Bauer GmbH', confidence: '0.920' },
      context: { step: 'comm_reference' },
      severity: 'normal',
      created_at: '2026-09-07T06:14:41.000Z',
    },
  ],
  'task-in1-rechnung': [
    {
      id: '9003',
      actor_kind: 'system',
      user_id: null,
      action: 'create',
      entity_type: 'task',
      entity_id: 'task-in1-rechnung',
      before: null,
      after: { process: 'invoice_check', routing: 'freigabe' },
      context: { comm_id: 'IN-1' },
      severity: 'normal',
      created_at: '2026-09-07T06:15:02.000Z',
    },
  ],
  'task-in1-schaden': [
    {
      id: '9004',
      actor_kind: 'system',
      user_id: null,
      action: 'create',
      entity_type: 'task',
      entity_id: 'task-in1-schaden',
      before: null,
      after: { process: 'email_intake', routing: 'freigabe' },
      context: { comm_id: 'IN-1' },
      severity: 'normal',
      created_at: '2026-09-07T06:15:02.000Z',
    },
  ],
  'task-in6-kuendigung': [
    {
      id: '9010',
      actor_kind: 'human',
      user_id: MOCK_USER_A,
      action: 'status_change',
      entity_type: 'task',
      entity_id: 'task-in6-kuendigung',
      before: { status: 'open' },
      after: { status: 'done' },
      context: null,
      severity: 'normal',
      created_at: '2026-09-04T16:02:00.000Z',
    },
  ],
  'task-in6-police': [
    {
      id: '9011',
      actor_kind: 'human',
      user_id: MOCK_USER_A,
      action: 'status_change',
      entity_type: 'task',
      entity_id: 'task-in6-police',
      before: { status: 'open' },
      after: { status: 'done' },
      context: null,
      severity: 'normal',
      created_at: '2026-09-04T16:20:00.000Z',
    },
  ],
  'IN-5': [
    {
      id: '9020',
      actor_kind: 'ai',
      user_id: null,
      action: 'status_change',
      entity_type: 'communication',
      entity_id: 'IN-5',
      before: { status: 'offen' },
      after: { status: 'erledigt', routing: 'automated' },
      context: { auto_threshold: '0.950', confidence: '0.960' },
      severity: 'normal',
      created_at: '2026-09-05T09:06:00.000Z',
    },
  ],
};

/** The audit rows of ONE entity (communication or task); empty when it has none. */
export function eingangAuditMock(entityId: string): AuditEntry[] {
  return EINGANG_AUDIT_MOCK[entityId] ?? [];
}

/** Demo status changes, in memory until reload; list, counts and header read them. */
const MOCK_EINGANG_STATUS = new Map<string, PosteingangState>();

/** The effective stored status of a fixture row in mock mode. */
export function mockEingangStatus(commId: string): PosteingangState {
  return (
    MOCK_EINGANG_STATUS.get(commId) ??
    POSTEINGANG_MOCK.find((item) => item.id === commId)?.eingang_status ??
    'offen'
  );
}

/** Record a status change made in mock mode. */
export function setMockEingangStatus(commId: string, status: PosteingangState): void {
  MOCK_EINGANG_STATUS.set(commId, status);
}

/** Test hygiene: forget every mock-mode status change. */
export function resetMockEingangStatus(): void {
  MOCK_EINGANG_STATUS.clear();
}

/** Demo access requests (participant view, with names); decisions live in memory. */
const FREIGABE_REQUESTS_FIXTURE: AccessRequestItem[] = [
  {
    id: 'FR-1',
    comm_id: 'IN-1',
    requested_by: 'u-lara',
    reviewed_by: null,
    status: 'pending',
    reviewed_at: null,
    created_at: '2026-09-29T08:12:00.000Z',
    requester_name: 'Lara Meier',
    reason: 'Familie Bauer ruft heute zurück, ich brauche die Schadenmeldung.',
    target_role: 'recipient',
    sync_level_at_request: 1,
    reviewer_name: null,
  },
  {
    id: 'FR-2',
    comm_id: 'IN-1',
    requested_by: 'u-marco',
    reviewed_by: null,
    status: 'pending',
    reviewed_at: null,
    created_at: '2026-09-29T10:40:00.000Z',
    requester_name: 'Marco Keller',
    reason: 'Ich übernehme die Werkstattrechnung in der Buchhaltung.',
    target_role: 'recipient',
    sync_level_at_request: 1,
    reviewer_name: null,
  },
  {
    id: 'FR-3',
    comm_id: 'IN-1',
    requested_by: 'u-sandra',
    reviewed_by: 'u-amelie',
    status: 'approved',
    reviewed_at: '2026-09-28T15:05:00.000Z',
    created_at: '2026-09-28T14:30:00.000Z',
    requester_name: 'Sandra Huber',
    reason: 'Vertretung während Ferienabwesenheit.',
    target_role: 'recipient',
    sync_level_at_request: 1,
    reviewer_name: 'Amelie',
  },
];

const MOCK_FREIGABE_DECISIONS = new Map<string, 'approved' | 'rejected'>();

/** The Freigabe-Anfragen of one demo Eingang, newest first, with demo decisions applied. */
export function freigabeRequestsMock(commId: string): AccessRequestItem[] {
  return FREIGABE_REQUESTS_FIXTURE.filter((r) => r.comm_id === commId)
    .map((r) => {
      const decided = MOCK_FREIGABE_DECISIONS.get(r.id);
      return decided
        ? { ...r, status: decided, reviewed_at: new Date().toISOString(), reviewer_name: 'Ich' }
        : r;
    })
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
}

/** Record a decision made in the demo (Erteilen / Ablehnen). */
export function decideFreigabeMock(requestId: string, decision: 'grant' | 'deny'): void {
  MOCK_FREIGABE_DECISIONS.set(requestId, decision === 'grant' ? 'approved' : 'rejected');
}

/** Test hygiene: forget every demo decision. */
export function resetFreigabeMock(): void {
  MOCK_FREIGABE_DECISIONS.clear();
}

/** Demo activity history; IN-1 carries a sent reply so the mail view can be reviewed. */
const DEMO_SENT_ID = 'OUT-1';

export function posteingangActivityMock(commId: string): PosteingangActivityEntry[] {
  const base = (id: string, at: string, content: string, actor: string | null = null): PosteingangActivityEntry => ({
    id,
    task_id: `${commId}-task`,
    actor_kind: actor ? 'human' : 'system',
    actor_id: null,
    type: actor ? 'note' : 'system',
    content,
    meta: null,
    created_at: at,
    actor_name: actor,
    sent: null,
  });
  if (commId !== 'IN-1') {
    return [base(`${commId}-a1`, '2026-09-07T06:14:00.000Z', 'Eingang erfasst')];
  }
  return [
    base('IN-1-a1', '2026-09-07T06:14:00.000Z', 'Eingang erfasst'),
    base('IN-1-a2', '2026-09-07T06:15:00.000Z', 'KI-Vorschlag erstellt'),
    base('IN-1-a3', '2026-09-28T09:30:00.000Z', 'Kunde telefonisch informiert', 'Lara Meier'),
    {
      ...base('IN-1-a4', '2026-09-29T20:45:00.000Z', 'Antwort versendet'),
      meta: { kind: 'email_sent', communication_id: DEMO_SENT_ID },
      actor_name: 'Amelie',
      sent: {
        communication_id: DEMO_SENT_ID,
        to: 'fam.bauer@bluewin.ch',
        subject: 'Re: Schaden Fahrzeug + Werkstattrechnung',
        attachment_count: 1,
      },
    },
  ];
}

/**
 * P2 hardening 05: the Systemanalyse pane's data. The Policenkopien-Helvetia Eingang (IN-2) produced a
 * police whose Selbstbehalt differs from the Antrag; every other demo Eingang has no controlled police.
 */
export function posteingangPolicenkontrolleMock(commId: string): PosteingangPolicenkontrolleEintrag[] {
  if (commId !== 'IN-2') return [];
  return [
    {
      placement_id: 'PL-2026-000512',
      placement_number: 'PL-2026-000512',
      placement_status: 'entschieden',
      closure_status: 'offen',
      policy_id: 'pol-mock',
      policy_number: 'HEL-2027-004711',
      carrier_name: 'Helvetia Versicherungen AG',
      kontrolle: {
        id: 'ctrl-mock',
        kontroll_nr: 1,
        is_aktuell: true,
        ausgeloest_durch: 'p1_ingest',
        ausgeloest_durch_label: 'Policeneingang',
        match_art: 'policennummer',
        match_art_label: 'Policennummer aus dem Underwriting',
        result: 'abweichung',
        zeilen: [
          { feld: 'selbstbehalt', label: 'Selbstbehalt', soll: 500, ist: 1000, status: 'abweichend', toleranz: null, hinweis: null },
          { feld: 'versicherungssumme', label: 'Versicherungssumme', soll: 5000000, ist: null, status: 'unbekannt', toleranz: null, hinweis: 'Nicht in der Police hinterlegt' },
        ],
        abweichungen_count: 1,
        unbekannt_count: 1,
        vollstaendig: false,
        policy_status_bei_kontrolle: 'active',
        befund: {
          result: 'abweichung',
          headline: 'Police HEL-2027-004711 weicht vom Antrag ab (1 Feld)',
          abweichungen: ["Abweichung im Bereich Selbstbehalt: Antrag CHF 500, Police CHF 1'000"],
          nicht_pruefbar: ['Versicherungssumme: in der Police nicht pruefbar'],
        },
        rueckmeldung: null,
        klaerung: { status: 'offen', status_label: 'Klaerung offen', task_id: 'task-mock', geklaert_at: null, geklaert_by_name: null, grund: null },
        ausgefuehrt_at: '2026-09-07T05:05:00.000Z',
        ausgefuehrt_by_name: null,
      },
      aktionen: { abweichung_klaeren: true, rueckmeldung_versicherer: true },
      empfaenger_vorschlag: 'scan-center@broker.ch',
      eingang_task_id: 'IN-2-task',
    },
  ];
}

/** The demo reply behind IN-1's sent entry. */
export function posteingangSentMailMock(commId: string, sentId: string): PosteingangSentMail | null {
  if (commId !== 'IN-1' || sentId !== DEMO_SENT_ID) return null;
  return {
    id: DEMO_SENT_ID,
    subject: 'Re: Schaden Fahrzeug + Werkstattrechnung',
    from: 'schaden@broker.ch',
    to: 'fam.bauer@bluewin.ch',
    cc: null,
    sent_at: '2026-09-29T20:45:00.000Z',
    sent_by_name: 'Amelie',
    body:
      'Guten Abend Familie Bauer\n\nVielen Dank für Ihre Schadenmeldung und die Werkstattrechnung. ' +
      'Wir haben beides an die Versicherung weitergeleitet und melden uns, sobald der Entscheid vorliegt.\n\n' +
      'Freundliche Grüsse\nAmelie',
    body_html: null,
    tier: 'full',
  };
}

/** Demo topics for Semantisch: query word stems → the attachment they "mean". */
const SEMANTIC_DEMO_TOPICS: ReadonlyArray<{ commId: string; filename: string; stems: string[] }> = [
  {
    commId: 'IN-1',
    filename: 'Schadenmeldung_Fahrzeug.pdf',
    stems: ['unfall', 'auto', 'fahrzeug', 'kollision', 'reparatur', 'werkstatt', 'schaden', 'blech'],
  },
  {
    commId: 'IN-2',
    filename: 'Police_Helvetia_884-1201.pdf',
    stems: ['police', 'vertrag', 'deckung', 'versicherungsschutz', 'helvetia', 'kopie'],
  },
  {
    commId: 'IN-4',
    filename: 'Maklermandat_unterschrieben.pdf',
    stems: ['mandat', 'vollmacht', 'unterschrift', 'auftrag', 'makler'],
  },
  {
    commId: 'IN-5',
    filename: 'Praemienrechnung_2026.pdf',
    stems: ['rechnung', 'praemie', 'prämie', 'zahlung', 'betrag', 'kosten'],
  },
];

/** The mock leg of GET /search?scope=posteingang: one hit per demo Eingang, by topic. */
export function posteingangSemanticMock(q: string): Array<{ id: string; filename: string }> {
  const query = q.toLowerCase();
  return SEMANTIC_DEMO_TOPICS.filter((t) => t.stems.some((stem) => query.includes(stem))).map((t) => ({
    id: t.commId,
    filename: t.filename,
  }));
}
